import { Banner, Tag } from '@components';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
  capitalize,
  useTheme,
} from '@mui/material';
import { Buildings } from 'iconsax-react';
import type { FC } from 'react';

import type { Product } from '@core/types/api/Product/Product';
import { hasValue } from '@core/utils/equality';
import { SlabCard } from '@web/components/SlabCard/SlabCard';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mismatchProducts?: Product[];
  onSaveAsDraft?: () => void;
  isApprovalFlowEnabled: boolean;
};
export const DispatchCustomerMismatchModal: FC<Props> = ({
  isOpen,
  onClose,
  mismatchProducts,
  onSaveAsDraft,
  isApprovalFlowEnabled,
}) => {
  const mispatchProductDispatchSystem = [
    ...new Set(
      (mismatchProducts ?? [])
        .map((product) => product.integrationSummary?.integrationType)
        .filter(hasValue),
    ),
  ];

  const mispatchProductDispatchSystemLabels = mispatchProductDispatchSystem
    .map((label) => capitalize(label))
    .join(', ');

  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      sx={{
        '& .MuiDialog-paper': {
          width: '900px',
          maxWidth: 'calc(100vw - 64px)',
        },
      }}
    >
      <DialogTitle variant='h4' sx={{ fontWeight: 500 }} id='alert-dialog-title'>
        Customer & Products Mismatch
      </DialogTitle>
      <DialogContent>
        <Box gap={2} display='flex' flexDirection='column'>
          <Banner severity='warning'>
            The selected customer and products belong to different dispatch systems.
          </Banner>
          {mismatchProducts && (
            <SlabCard
              sx={{
                backgroundColor: theme.palette.SlabGray[50],
                padding: 1.5,
                my: 0,
                gap: 1,
              }}
            >
              <Typography variant='h5/medium'>Cannot export these products:</Typography>
              {mismatchProducts.map((product) => {
                return (
                  <SlabCard key={product.id} sx={{ padding: 1.5, my: 0, gap: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant='h6/medium'>{product.name}</Typography>
                      </Box>
                      {hasValue(product.integrationSummary) && (
                        <Tag
                          variant='Secondary'
                          label={capitalize(product.integrationSummary.integrationType)}
                          icon={Buildings}
                          iconColor={theme.palette.SlabIndigo[700]}
                        />
                      )}
                    </Box>
                  </SlabCard>
                );
              })}
            </SlabCard>
          )}

          <SlabCard
            sx={{ padding: 1.5, my: 0, gap: 1, backgroundColor: theme.palette.SlabBlue.IceLight }}
          >
            <Typography variant='h5/medium'>Resolution Options</Typography>
            {isApprovalFlowEnabled ? (
              <>
                <SlabCard sx={{ padding: 1.5, my: 0 }}>
                  <Typography variant='h6/medium'>
                    OPTION 1: Create customer in {mispatchProductDispatchSystemLabels} first
                  </Typography>
                </SlabCard>
                <SlabCard sx={{ padding: 1.5, my: 0 }}>
                  <Typography variant='h6/medium'>
                    OPTION 2: Create a revision to remove {mispatchProductDispatchSystemLabels}{' '}
                    products
                  </Typography>
                  <List
                    sx={{
                      color: theme.palette.SlabGray[600],
                      listStyleType: 'disc',
                      pl: 3,
                      '& .MuiListItem-root': {
                        display: 'list-item',
                        pl: 1,
                      },
                    }}
                  >
                    <ListItem>
                      <Typography variant='h6/medium'>
                        Create a new quote revision without {mispatchProductDispatchSystemLabels}{' '}
                        products
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant='h6/medium'>Submit for approval if required</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant='h6/medium'>Export after approval</Typography>
                    </ListItem>
                  </List>
                </SlabCard>
              </>
            ) : (
              <>
                <SlabCard sx={{ padding: 1.5, my: 0 }}>
                  <Typography variant='h6/medium'>
                    OPTION 1: Remove incompatible products
                  </Typography>
                  <List
                    sx={{
                      color: theme.palette.SlabGray[600],
                      listStyleType: 'disc',
                      pl: 3,
                      '& .MuiListItem-root': {
                        display: 'list-item',
                        pl: 1,
                      },
                    }}
                  >
                    <ListItem>
                      <Typography variant='h6/medium'>
                        Edit the quote to remove products from a different dispatch system.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant='h6/medium'>Submit for approval if required</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant='h6/medium'>Export after approval </Typography>
                    </ListItem>
                  </List>
                </SlabCard>
                <SlabCard sx={{ padding: 1.5, my: 0 }}>
                  <Typography variant='h6/medium'>
                    OPTION 2: Align customer with products
                  </Typography>
                  <List
                    sx={{
                      color: theme.palette.SlabGray[600],
                      listStyleType: 'disc',
                      pl: 3,
                      '& .MuiListItem-root': {
                        display: 'list-item',
                        pl: 1,
                      },
                    }}
                  >
                    <ListItem>
                      <Typography variant='h6/medium'>
                        Create or select the customer in the same dispatch system as the products,
                        then return to export this quote.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant='h6/medium'>Wait for sync (~20 minutes)</Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant='h6/medium'>Return to export this quote</Typography>
                    </ListItem>
                  </List>
                </SlabCard>
              </>
            )}
          </SlabCard>
        </Box>
      </DialogContent>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 3,
          py: 2,
        }}
      >
        <Button variant='slab-secondary' sx={{ flex: 1 }} onClick={onClose}>
          Cancel
        </Button>

        {onSaveAsDraft ? (
          <Button variant='slab-secondary' sx={{ flex: 1 }} onClick={onSaveAsDraft}>
            Save as Draft
          </Button>
        ) : (
          <Button variant='slab-primary' sx={{ flex: 1 }} onClick={onClose}>
            Ok
          </Button>
        )}
      </Box>
    </Dialog>
  );
};
